/**
 * The per-`property_type` baseline equipment template (Story 12.2, R-P3,
 * spec judgement calls 1-2) — a plain, hand-mirrored copy of `app/domain/
 * equipment/baseline_templates.py`'s own real constant, kept in sync by
 * hand, on purpose, rather than fetched over a new route this story does
 * not add (VC-12.2-12/13): the identical "referenced by its own string on
 * both sides, never imported across the frontend/backend boundary"
 * discipline `AddPropertyWizard.tsx`'s own `SINGLE_FAMILY_PROPERTY_TYPE`
 * constant already established (VC-5.4-15).
 *
 * R-P3's own per-property-type table: `sfh` gets its four items on its one
 * real unit; `two_family`/`three_family` get one shared, building-level
 * `boiler` plus a `refrigerator` per real unit (never a per-unit `boiler`,
 * never `washer`/`dryer`); `condo` gets only `refrigerator`. `mixed_use`
 * (and any unrecognized property type) is deliberately absent from
 * `BASELINE_EQUIPMENT_TEMPLATES` — `baselineTemplateForPropertyType`'s own
 * `?? []` fallback already returns the same honest empty array a
 * present-but-empty entry would say.
 */

export const PROPERTY_TYPE_SFH = "sfh";
export const PROPERTY_TYPE_TWO_FAMILY = "two_family";
export const PROPERTY_TYPE_THREE_FAMILY = "three_family";
export const PROPERTY_TYPE_CONDO = "condo";
export const PROPERTY_TYPE_MIXED_USE = "mixed_use";

//: The ten real, seeded `equipment_category` values this template proposes
//: from (`app/domain/equipment/useful_life_defaults.py`'s own identical
//: `EQUIPMENT_CATEGORY_*` constants) — named once here so a typo can never
//: silently diverge from the real seeded reference list.
export const EQUIPMENT_CATEGORY_FURNACE = "furnace";
export const EQUIPMENT_CATEGORY_BOILER = "boiler";
export const EQUIPMENT_CATEGORY_REFRIGERATOR = "refrigerator";
export const EQUIPMENT_CATEGORY_WASHER = "washer";
export const EQUIPMENT_CATEGORY_DRYER = "dryer";

export interface BaselineTemplateRow {
  category: string;
  isBuildingLevel: boolean;
}

export const BASELINE_EQUIPMENT_TEMPLATES: Record<string, BaselineTemplateRow[]> = {
  [PROPERTY_TYPE_SFH]: [
    { category: EQUIPMENT_CATEGORY_FURNACE, isBuildingLevel: false },
    { category: EQUIPMENT_CATEGORY_REFRIGERATOR, isBuildingLevel: false },
    { category: EQUIPMENT_CATEGORY_WASHER, isBuildingLevel: false },
    { category: EQUIPMENT_CATEGORY_DRYER, isBuildingLevel: false },
  ],
  [PROPERTY_TYPE_TWO_FAMILY]: [
    { category: EQUIPMENT_CATEGORY_BOILER, isBuildingLevel: true },
    { category: EQUIPMENT_CATEGORY_REFRIGERATOR, isBuildingLevel: false },
  ],
  [PROPERTY_TYPE_THREE_FAMILY]: [
    { category: EQUIPMENT_CATEGORY_BOILER, isBuildingLevel: true },
    { category: EQUIPMENT_CATEGORY_REFRIGERATOR, isBuildingLevel: false },
  ],
  [PROPERTY_TYPE_CONDO]: [{ category: EQUIPMENT_CATEGORY_REFRIGERATOR, isBuildingLevel: false }],
};

/** The real baseline proposal for `propertyType`, or an empty array for
 * `mixed_use` (deliberately absent above) or any unrecognized value — both
 * resolve the identical honest "nothing proposed" shape. */
export function baselineTemplateForPropertyType(propertyType: string): BaselineTemplateRow[] {
  return BASELINE_EQUIPMENT_TEMPLATES[propertyType] ?? [];
}
